import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { invalidateInvoicePaymentViews } from '../invoices/invalidateInvoicePaymentViews';
import { contactKeys, creditMemoKeys, invoiceKeys } from '@/lib/queryKeys';
import { billingV2Endpoints } from '@/lib/api/endpoints';
import { billingV2Post } from '@/lib/api/clients/billing-v2';
import type { ApplyCreditMemoInput, ApplyCreditMemoResult } from '@/types/creditMemo';

export function useApplyCreditMemo(creditMemoId: string, contactId?: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ApplyCreditMemoResult, ApplyCreditMemoInput>({
    mutationFn: (input) =>
      billingV2Post<ApplyCreditMemoResult, ApplyCreditMemoInput>(
        billingV2Endpoints.creditMemoApply(creditMemoId),
        input,
      ),
    onSuccess: (result) => {
      // The memo's remaining credit is also the customer's available credit
      // and every open invoice's "Apply memo" suggestion — separate queries
      // under the credit-memo prefix. Refreshing only the memo's own detail
      // and lists left those offering credit that was already spent.
      void queryClient.invalidateQueries({ queryKey: creditMemoKeys.all() });
      void queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(result.invoiceId),
      });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      void invalidateInvoicePaymentViews(queryClient);
      if (contactId) {
        void queryClient.invalidateQueries({ queryKey: contactKeys.detail(contactId) });
      }
      toast.success(
        `Applied $${result.amountApplied.toFixed(2)} — $${result.remainingCredit.toFixed(2)} credit remaining`,
      );
    },
  });
}
